// /src/Components/Domain/CreateDomain.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FileJson,
  Loader2,
  Sparkles,
} from "lucide-react";
import { toast } from "react-hot-toast";

import DomainSetupForm from "./components/DomainSetupForm";
import DeploymentPipeline from "./components/DeploymentPipeline";
import DeploymentResult from "./components/DeploymentResult";
import WebsitePreview from "./components/WebsitePreview";

// ======================================================
// Fake terminal messages shown during real API execution
// ======================================================

const TERMINAL_LINES = [
  "npm install @chaafo/domain-runtime --production",
  "resolving production DNS configuration...",
  "writing server A record...",
  "creating cPanel workspace...",
  "mounting restaurant subdomain directory...",
  "installing selected template bundle...",
  "copying theme assets and runtime files...",
  "generating deployment manifest...",
  "binding website with selected client...",
  "running production verification checks...",
  "warming website route...",
];

export default function CreateDomain({
  domain,
  fixedContentIp,

  templates = [],
  templatesLoading,
  fetchTemplates,

  selectedClientId,
  setSelectedClientId,

  selectedTemplateId,
  setSelectedTemplateId,

  selectedTemplateCode,
  setSelectedTemplateCode,

  subdomainName,
  setSubdomainName,

  creating,
  createdDomainData,
  deploymentSteps,

  createDomainFlow,
  resetCreateForm,
}) {
  const [terminalText, setTerminalText] = useState(
    "ready: waiting for deployment command",
  );

  // ======================================================
  // Derived domain values
  // ======================================================

  const cleanSubdomain = String(subdomainName || "")
    .trim()
    .toLowerCase();

  const dynamicHost = cleanSubdomain
    ? `${cleanSubdomain}.${domain}`
    : `restaurant.${domain}`;

  const previewFinalUrl = `https://${dynamicHost}`;

  const finalUrl = createdDomainData?.finalUrl || "";

  // ======================================================
  // Rapid terminal text animation
  // ======================================================

  useEffect(() => {
    if (!creating) {
      setTerminalText(
        createdDomainData
          ? "deployment completed with exit code 0"
          : "ready: waiting for deployment command",
      );

      return;
    }

    let index = 0;

    setTerminalText(TERMINAL_LINES[0]);

    const timer = window.setInterval(() => {
      index = (index + 1) % TERMINAL_LINES.length;
      setTerminalText(TERMINAL_LINES[index]);
    }, 500);

    return () => {
      window.clearInterval(timer);
    };
  }, [creating, createdDomainData]);

  // ======================================================
  // Form readiness
  // ======================================================

  const canCreate = useMemo(() => {
    return Boolean(
      selectedClientId &&
        selectedTemplateId &&
        selectedTemplateCode &&
        subdomainName?.trim() &&
        !creating &&
        !templatesLoading,
    );
  }, [
    selectedClientId,
    selectedTemplateId,
    selectedTemplateCode,
    subdomainName,
    creating,
    templatesLoading,
  ]);

  // ======================================================
  // Clipboard handlers
  // ======================================================

  const copyText = async (value, message) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      toast.success(message);
    } catch (error) {
      console.error("Clipboard error:", error);
      toast.error("Could not copy value");
    }
  };

  const handleCopyDomain = () => {
    copyText(
      createdDomainData?.fullDomain,
      "Domain copied",
    );
  };

  const handleCopyFinalUrl = () => {
    copyText(finalUrl, "Final URL copied");
  };

  return (
    <div className="rounded-2xl border bg-white p-3">
      {/* ==================================================
          Main create-domain card
      ================================================== */}

      <div className="rounded-2xl border bg-gradient-to-br from-emerald-50 via-white to-white p-4">
        {/* Page heading */}
        <div className="mb-5 flex flex-col justify-between gap-3 md:flex-row md:items-center">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-semibold text-emerald-700">
              <Sparkles size={14} />
              Chaafo Domain Deployment
            </div>

            <h2 className="text-lg font-semibold text-gray-900">
              Create Restaurant Subdomain
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Select a client, choose a template and enter the
              restaurant subdomain. Server configuration is applied
              automatically.
            </p>
          </div>

          <button
            type="button"
            onClick={resetCreateForm}
            disabled={creating}
            className="inline-flex h-10 items-center justify-center rounded-xl border bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Reset
          </button>
        </div>

        {/* Client, template, subdomain and fixed settings */}
        <DomainSetupForm
          domain={domain}
          fixedContentIp={fixedContentIp}
          templates={templates}
          templatesLoading={templatesLoading}
          fetchTemplates={fetchTemplates}
          selectedClientId={selectedClientId}
          setSelectedClientId={setSelectedClientId}
          selectedTemplateId={selectedTemplateId}
          setSelectedTemplateId={setSelectedTemplateId}
          selectedTemplateCode={selectedTemplateCode}
          setSelectedTemplateCode={setSelectedTemplateCode}
          subdomainName={subdomainName}
          setSubdomainName={setSubdomainName}
          creating={creating}
        />

        {/* Real API deployment progress */}
        <DeploymentPipeline
          creating={creating}
          deploymentSteps={deploymentSteps}
          terminalText={terminalText}
          dynamicHost={dynamicHost}
          selectedTemplateCode={selectedTemplateCode}
          selectedClientId={selectedClientId}
        />

        {/* Manifest failure fallback message */}
        {deploymentSteps?.manifest === "error" && (
          <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-amber-100 text-amber-700">
                <FileJson size={18} />
              </div>

              <div>
                <h4 className="text-sm font-semibold text-amber-900">
                  Automatic Manifest Update Failed
                </h4>

                <p className="mt-1 text-sm leading-6 text-amber-800">
                  The automatic update was attempted twice but could
                  not be completed. Wait for the website to stabilize,
                  then open the <strong>Websites</strong> section and
                  use <strong>Update Manifest</strong>.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Final target and submit button */}
        <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="rounded-xl border bg-white px-3 py-2 text-sm text-gray-600">
            Final deployment target{" "}
            <span className="break-all font-mono font-semibold text-gray-900">
              {previewFinalUrl}
            </span>

            <p className="mt-1 text-xs text-gray-400">
              Content IP {fixedContentIp} will be applied
              automatically.
            </p>
          </div>

          <button
            type="button"
            onClick={createDomainFlow}
            disabled={!canCreate}
            className="inline-flex h-11 min-w-[230px] items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#00ac6e] disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {creating ? (
              <>
                <Loader2 size={17} className="animate-spin" />
                Deploying...
              </>
            ) : (
              <>
                <Sparkles size={17} />
                Create & Deploy Domain
              </>
            )}
          </button>
        </div>
      </div>

      {/* Deployment summary */}
      <DeploymentResult
        createdDomainData={createdDomainData}
        finalUrl={finalUrl}
        onCopyDomain={handleCopyDomain}
        onCopyFinalUrl={handleCopyFinalUrl}
      />

      {/* Full-height website iframe */}
      {createdDomainData && (
        <WebsitePreview
          finalUrl={finalUrl}
          onCopyFinalUrl={handleCopyFinalUrl}
        />
      )}
    </div>
  );
}